import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, IconButton, Typography, CircularProgress, Skeleton } from '@mui/material';
import { Close as CloseIcon, AddComment as AddCommentIcon, Forum as ForumIcon } from '@mui/icons-material';
import { send_request } from '../../../utils/Request';
import { loadDraftProject } from '../../../utils/canvasApi';
import { v4 as uuid } from 'uuid';
import AgentSessionsPanel from '../AgentSessionsPanel';
import SessionRow from '../SessionRow';
import useAgentStore from '../useAgentStore';
import useCanvasStore from './useCanvasStore';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import { ComponentFactory } from '../../ProjectCanvas/ComponentRegistry';
import { EDGE_TYPES } from '../../../utils/CanvasConstants';
import { storeMap } from '../../../utils/prefillComponentStores';
import { protectLoadedStore } from '../../../utils/storeWriteGuard';
import { applyDagreLayouting } from '../../../utils/layoutUtils';

function buildNodeFromPayload(payload) {
  const template = ComponentFactory[payload.type] || {};
  const position = payload.position || { x: 250, y: 250 };
  return {
    id: payload.componentId,
    type: 'component',
    position: { x: Number(position.x) || 0, y: Number(position.y) || 0 },
    data: {
      componentId: payload.componentId,
      type: payload.type,
      label: payload.name || template.displayName || payload.type,
      displayName: template.displayName || payload.type,
      logo: template.logo || '',
      hasInput: template.hasInput || false,
      hasOutput: template.hasOutput || false,
      baseColor: template.baseColor || '#868686',
    },
  };
}

function buildEdgeFromPayload(payload) {
  const edgeType = payload.edgeType === 'loopLink' ? EDGE_TYPES.LOOPLINK : EDGE_TYPES.LINK;
  const color = edgeType === EDGE_TYPES.LOOPLINK ? '#9c27b0' : '#868686';
  return {
    id: uuid(),
    source: payload.sourceComponentId,
    target: payload.targetComponentId,
    type: edgeType,
    markerEnd: { type: 'arrowclosed', color },
    style: { strokeWidth: '3px', stroke: color },
    label: edgeType === EDGE_TYPES.LINK ? 'componentLink' : undefined,
  };
}

/**
 * Refetch components from the backend and sync both project store and canvas store.
 * Called when the agent session completes after canvas mutations.
 */
async function refetchProjectComponents(projectId) {
  try {
    const res = await loadDraftProject(projectId);
    if (!res?.data) return;

    const workflowData = res.data;
    const cs = useCanvasStore.getState();
    const ps = useProjectStore.getState();

    // Preserve non-component items (notes, note links)
    const notesAndLinks = [
      ...cs.nodes.filter(item => item.type === 'note'),
      ...cs.edges.filter(item => item.label !== 'componentLink'),
    ];

    // Update project store with fresh components
    useProjectStore.setState({ components: workflowData.components });
    const canvas = ps.readDiagram(workflowData.components);
    useProjectStore.setState({ canvas });

    // Set componentId on each node's data
    const eles = canvas.map(c => {
      if ((c.type === 'component' || c.type === 'dbNode') && c.data) {
        c.data['componentId'] = c.id;
      }
      return c;
    });

    useCanvasStore.getState().setNodesAndEdges([...eles, ...notesAndLinks]);
  } catch (e) {
    console.warn('[WorkflowAgentChat] Failed to refetch components after agent session', e);
  }
}

export default function WorkflowAgentChat({ projectId, projectName, onClose }) {
  const [agentId, setAgentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentEditable, setAgentEditable] = useState(null); // { editable, reason, legacyComponents }
  const [newChatNonce, setNewChatNonce] = useState(0);
  const [showSessionsOverlay, setShowSessionsOverlay] = useState(false);
  const [selectSessionRequest, setSelectSessionRequest] = useState(null);
  const canvasMutatedRef = useRef(false);

  const allSessions = useAgentStore(state => state.sessions);
  const sessionsLoading = useAgentStore(state => state.sessionsLoading);
  const sessions = useMemo(
    () => allSessions.filter(s => s.projectId === projectId),
    [allSessions, projectId]
  );

  const handleOpenSessions = useCallback(() => {
    if (agentId) {
      useAgentStore.getState().fetchSessions(agentId);
    }
    setShowSessionsOverlay(true);
  }, [agentId]);

  const handleSelectSession = useCallback((chatSessionId) => {
    setShowSessionsOverlay(false);
    setSelectSessionRequest({ sessionId: chatSessionId, nonce: Date.now() });
  }, []);

  const aiActiveTimersRef = useRef({});

  const markAiActiveComponent = useCallback((componentId) => {
    if (!componentId) return;
    useCanvasStore.getState().addAiActiveComponent(componentId);
    // Clear any existing timer for this component
    if (aiActiveTimersRef.current[componentId]) {
      clearTimeout(aiActiveTimersRef.current[componentId]);
    }
    // Auto-clear after 8s as a fallback
    aiActiveTimersRef.current[componentId] = setTimeout(() => {
      useCanvasStore.getState().removeAiActiveComponent(componentId);
      delete aiActiveTimersRef.current[componentId];
    }, 8000);
  }, []);

  const handleCanvasUpdate = useCallback((payload) => {
    if (!payload || !payload.action) return;
    const { action } = payload;
    if (payload.componentId) {
      markAiActiveComponent(payload.componentId);
    }

    if (action === '_tool_activity_start') {
      return;
    }

    canvasMutatedRef.current = true;

    switch (action) {
      case 'add_component': {
        if (!payload.componentId) break;
        const node = buildNodeFromPayload(payload);
        useCanvasStore.getState().addNode(node);
        // Also add to useProjectStore so placeholder computation works during session
        const existingComponents = useProjectStore.getState().components || [];
        if (!existingComponents.some(c => c.componentId === payload.componentId)) {
          useProjectStore.setState({
            components: [...existingComponents, {
              componentId: payload.componentId,
              type: payload.type,
              viewData: node.position,
              nextComponents: [],
            }],
          });
        }
        break;
      }
      case 'delete_component': {
        if (!payload.componentId) break;
        const compId = payload.componentId;
        useCanvasStore.getState().setNodesFn(nodes =>
          nodes.filter(n => n.id !== compId)
        );
        useCanvasStore.getState().setEdgesFn(edges =>
          edges.filter(e => e.source !== compId && e.target !== compId)
        );
        break;
      }
      case 'rename_component': {
        if (!payload.componentId) break;
        useCanvasStore.getState().setNodesFn(nodes =>
          nodes.map(n =>
            n.id === payload.componentId
              ? { ...n, data: { ...n.data, label: payload.name } }
              : n
          )
        );
        break;
      }
      case 'add_edge': {
        if (!payload.sourceComponentId || !payload.targetComponentId) break;
        const edge = buildEdgeFromPayload(payload);
        useCanvasStore.getState().addEdge(edge);
        break;
      }
      case 'delete_edge': {
        if (!payload.sourceComponentId || !payload.targetComponentId) break;
        useCanvasStore.getState().setEdgesFn(edges =>
          edges.filter(e =>
            !(e.source === payload.sourceComponentId && e.target === payload.targetComponentId)
          )
        );
        break;
      }
      case 'layout': {
        const { nodes, edges } = useCanvasStore.getState();
        if (nodes && nodes.length > 0) {
          const result = applyDagreLayouting(nodes, edges);
          useCanvasStore.getState().setNodesAndEdges(result);
        }
        break;
      }
      case 'update_component_data': {
        if (!payload.componentId || !payload.rawComponentData) break;
        const compId = payload.componentId;
        const rawData = payload.rawComponentData;

        // Update useProjectStore.components with new rawComponentData
        const comps = useProjectStore.getState().components || [];
        const found = comps.some(c => c.componentId === compId);
        if (found) {
          useProjectStore.setState({
            components: comps.map(c =>
              c.componentId === compId
                ? { ...c, rawComponentData: rawData }
                : c
            ),
          });
        } else {
          // Component not yet in project store — append it
          useProjectStore.setState({
            components: [...comps, { componentId: compId, rawComponentData: rawData }],
          });
        }

        // Sync keyed component store so config panel reflects changes immediately
        try {
          const comp = comps.find(c => c.componentId === compId);
          const compType = comp?.type;
          if (compType && storeMap[compType]) {
            const config = storeMap[compType];
            if (config.getStore && config.setter) {
              const keyedStore = config.getStore(compId);
              const state = keyedStore.getState();
              if (typeof state[config.setter] === 'function') {
                state[config.setter]({
                  componentId: compId,
                  type: compType,
                  description: '',
                  componentData: rawData,
                });
                keyedStore.setState({ loaded: true, name: rawData.name || '' });
                protectLoadedStore(keyedStore);
              }
            }
          }
        } catch (e) {
          console.warn('[WorkflowAgentChat] Failed to sync keyed store for component update', e);
        }
        break;
      }
      default:
        break;
    }
  }, []);

  // Register / unregister canvas update handler
  useEffect(() => {
    useAgentStore.getState().setCanvasUpdateHandler(handleCanvasUpdate);
    return () => useAgentStore.getState().setCanvasUpdateHandler(null);
  }, [handleCanvasUpdate]);

  // Watch for agent session completion — refetch components if canvas was mutated
  const prevStatusRef = useRef(null);
  useEffect(() => {
    const unsub = useAgentStore.subscribe((state) => {
      const status = state.sessionDetail?.status;
      const prev = prevStatusRef.current;
      prevStatusRef.current = status;
      if (
        prev === 'IN_PROGRESS' &&
        status &&
        status !== 'IN_PROGRESS'
      ) {
        // Clear AI active component highlights
        Object.values(aiActiveTimersRef.current).forEach(clearTimeout);
        aiActiveTimersRef.current = {};
        useCanvasStore.getState().clearAiActiveComponents();

        if (canvasMutatedRef.current) {
          canvasMutatedRef.current = false;
          refetchProjectComponents(projectId);
        }
      }
    });
    return unsub;
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Check agent-editable gate and provision in parallel
        const [editableRes, provisionRes] = await Promise.all([
          send_request(
            `project-service/v1/workflow-builder/${projectId}/agent-editable`,
            {},
            {},
            'GET'
          ),
          send_request(
            'project-service/v1/workflow-builder/agent/provision',
            {},
            {},
            'POST'
          ),
        ]);
        if (cancelled) return;

        if (editableRes?.data) {
          setAgentEditable(editableRes.data);
        }

        if (provisionRes?.data?.agentId) {
          setAgentId(provisionRes.data.agentId);
        } else {
          setError('Failed to provision workflow builder agent');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to provision agent');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const chatContext = useMemo(
    () => ({ projectId, projectName }),
    [projectId, projectName]
  );

  const panelStyle = {
    width: 420,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    position: 'relative',
    zIndex: 5,
    flexShrink: 0,
  };

  if (loading) {
    return (
      <Box sx={{ ...panelStyle, alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...panelStyle, alignItems: 'center', justifyContent: 'center', gap: 1, p: 2 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (agentEditable && agentEditable.editable === false) {
    return (
      <Box sx={panelStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            height: '57px',
            minHeight: '57px',
            maxHeight: '57px',
            boxSizing: 'border-box',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Workflow Builder AI
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            {agentEditable.reason || 'This workflow cannot be edited by the AI builder.'}
          </Typography>
          {agentEditable.legacyComponents && agentEditable.legacyComponents.length > 0 && (
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Legacy components:
              </Typography>
              {agentEditable.legacyComponents.map((name, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {name}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={panelStyle}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          height: '57px',
          minHeight: '57px',
          maxHeight: '57px',
          boxSizing: 'border-box',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Workflow Builder AI
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setNewChatNonce(n => n + 1)} title="New chat">
            <AddCommentIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleOpenSessions} title="Sessions">
            <ForumIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <AgentSessionsPanel
          agentId={agentId}
          agentName="Workflow Builder"
          agentDescription="AI assistant for building and editing workflows"
          mode="private"
          showSessionSidebar={false}
          newChatRequestNonce={newChatNonce}
          selectSessionRequest={selectSessionRequest}
          inlineTaskQueueToggle={false}
          chatContext={chatContext}
          chatUiPolicy={{
            showSystemPromptInChat: false,
            showToolCallsAndOutputsInChat: true,
            showInterAgentMessagesInChat: false,
          }}
        />

        {showSessionsOverlay && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'background.paper',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                Sessions
              </Typography>
              <IconButton size="small" onClick={() => setShowSessionsOverlay(false)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {sessionsLoading ? (
                <Box sx={{ p: 2 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Skeleton variant="text" width="80%" height={18} />
                      <Skeleton variant="text" width="50%" height={14} />
                    </Box>
                  ))}
                </Box>
              ) : sessions.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.6 }}>
                    No sessions yet.
                  </Typography>
                </Box>
              ) : (
                sessions.map((s) => (
                  <SessionRow
                    key={s.chatSessionId}
                    session={s}
                    selected={false}
                    onClick={() => handleSelectSession(s.chatSessionId)}
                    showWorkflowProvenanceInChat={false}
                  />
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
